import { useState, useMemo, useEffect, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
  alpha,
  Avatar,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WarningIcon from '@mui/icons-material/Warning';
import axiosInstance from '../../lib/axios';
import { TableToolbar, FilterOption, ColumnConfig } from '../../components/TableToolbar';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { Delivery, DeliveryStatus } from '../../types';

const fetchDeliveries = async (page: number, pageSize: number) => {
  const params: Record<string, any> = {
    page: page + 1,
    limit: pageSize,
  };

  const response = await axiosInstance.get(`/deliveries`, { params });

  return {
    data: Array.isArray(response.data) ? response.data : response.data.data || [],
    total: response.data.total || (Array.isArray(response.data) ? response.data.length : 0),
  };
};

const filterOptions: FilterOption[] = [
  {
    field: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'pending', label: 'Pending' },
      { value: 'assigned', label: 'Assigned' },
      { value: 'picked_up', label: 'Picked Up' },
      { value: 'out_for_delivery', label: 'Out for Delivery' },
      { value: 'delivered', label: 'Delivered' },
      { value: 'failed', label: 'Failed' },
      { value: 'cancelled', label: 'Cancelled' },
    ],
  },
];

const exportColumns: ColumnConfig[] = [
  { field: 'id', headerName: 'Delivery ID' },
  { field: 'customerName', headerName: 'Customer' },
  {
    field: 'deliveryAddress',
    headerName: 'Address',
    valueFormatter: (value) => {
      if (!value) return 'N/A';
      return `${value.street}, ${value.city}`;
    },
  },
  { field: 'status', headerName: 'Status' },
  {
    field: 'driver',
    headerName: 'Driver',
    valueFormatter: (value) => {
      if (!value) return 'Unassigned';
      return `${value.firstName} ${value.lastName}`;
    },
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    valueFormatter: (value) => new Date(value).toLocaleString('fr-DZ'),
  },
];

export const DeliveryOverview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  // Read status from URL query parameter on mount
  useEffect(() => {
    const statusParam = searchParams.get('status');
    if (statusParam) {
      setActiveFilters((prev) => ({ ...prev, status: statusParam }));
    }
  }, [searchParams]);

  const { data, isLoading } = useQuery({
    queryKey: ['deliveries', page, pageSize],
    queryFn: () => fetchDeliveries(page, pageSize),
  });

  const filteredData = useMemo(() => {
    if (!data?.data) return [];

    return data.data.filter((item: Delivery) => {
      // Search filter
      if (searchValue) {
        const search = searchValue.toLowerCase();
        const matchesSearch =
          item.id?.toLowerCase().includes(search) ||
          item.customerName?.toLowerCase().includes(search) ||
          item.customerPhone?.toLowerCase().includes(search) ||
          item.deliveryAddress?.street?.toLowerCase().includes(search) ||
          item.deliveryAddress?.city?.toLowerCase().includes(search) ||
          item.driver?.firstName?.toLowerCase().includes(search) ||
          item.driver?.lastName?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      // Status filter
      if (activeFilters.status && item.status !== activeFilters.status) {
        return false;
      }

      return true;
    });
  }, [data?.data, searchValue, activeFilters]);

  const getStatusColor = (status: DeliveryStatus): string => {
    switch (status) {
      case 'pending':
        return palette.warning;
      case 'assigned':
        return palette.info;
      case 'picked_up':
        return palette.accent;
      case 'out_for_delivery':
        return '#9C27B0'; // Purple
      case 'delivered':
        return palette.success;
      case 'failed':
        return palette.error;
      case 'cancelled':
        return palette.textMuted;
      default:
        return palette.textMuted;
    }
  };

  const getStatusIcon = (status: DeliveryStatus) => {
    switch (status) {
      case 'pending':
        return <HourglassEmptyIcon sx={{ fontSize: 16 }} />;
      case 'assigned':
        return <AssignmentIndIcon sx={{ fontSize: 16 }} />;
      case 'picked_up':
        return <LocalShippingIcon sx={{ fontSize: 16 }} />;
      case 'out_for_delivery':
        return <DirectionsCarIcon sx={{ fontSize: 16 }} />;
      case 'delivered':
        return <CheckCircleIcon sx={{ fontSize: 16 }} />;
      case 'failed':
        return <WarningIcon sx={{ fontSize: 16 }} />;
      case 'cancelled':
        return <CancelIcon sx={{ fontSize: 16 }} />;
      default:
        return null;
    }
  };

  const formatStatusLabel = (status: DeliveryStatus): string => {
    return status.replace(/_/g, ' ').toUpperCase();
  };

  const handleFilterChange = (field: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setActiveFilters({});
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDateTime = (dateString: string | undefined) => {
    if (!dateString) return { date: '-', time: '' };
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('fr-DZ', { day: '2-digit', month: 'short', year: 'numeric' }),
      time: date.toLocaleTimeString('fr-DZ', { hour: '2-digit', minute: '2-digit' }),
    };
  };

  const formatAddress = (address: Delivery['deliveryAddress'] | undefined) => {
    if (!address) return 'No address';
    return `${address.street}, ${address.city}`;
  };

  // Calculate statistics
  const stats = useMemo(() => {
    const total = filteredData.length;
    const pending = filteredData.filter((item: Delivery) => item.status === 'pending').length;
    const assigned = filteredData.filter((item: Delivery) => item.status === 'assigned').length;
    const outForDelivery = filteredData.filter(
      (item: Delivery) => item.status === 'out_for_delivery'
    ).length;
    const delivered = filteredData.filter((item: Delivery) => item.status === 'delivered').length;
    const failed = filteredData.filter((item: Delivery) => item.status === 'failed').length;

    return { total, pending, assigned, outForDelivery, delivered, failed };
  }, [filteredData]);

  const colors =
    mode === 'dark'
      ? {
          bg: palette.background,
          surface: palette.surface,
          surfaceElevated: palette.surfaceElevated,
          border: palette.border,
          borderLight: palette.borderLight,
          textPrimary: palette.textPrimary,
          textSecondary: palette.textSecondary,
          textMuted: palette.textMuted,
        }
      : {
          bg: palette.light.background,
          surface: palette.light.surface,
          surfaceElevated: palette.light.surfaceElevated,
          border: palette.light.border,
          borderLight: palette.light.borderLight,
          textPrimary: palette.light.textPrimary,
          textSecondary: palette.light.textSecondary,
          textMuted: palette.light.textMuted,
        };

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Stats Dashboard */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 2,
          mb: 3,
          ...animations.slideUp(0.1),
        }}
      >
        {[
          { label: 'TOTAL', value: stats.total, color: palette.primary },
          { label: 'PENDING', value: stats.pending, color: palette.warning },
          { label: 'ASSIGNED', value: stats.assigned, color: palette.info },
          { label: 'OUT FOR DELIVERY', value: stats.outForDelivery, color: '#9C27B0' },
          { label: 'DELIVERED', value: stats.delivered, color: palette.success },
          { label: 'FAILED', value: stats.failed, color: palette.error },
        ].map((stat) => (
          <Card
            key={stat.label}
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 2,
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 24px ${alpha(stat.color, 0.2)}`,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: stat.color,
              },
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: '10px',
                letterSpacing: typography.letterSpacing.widest,
                fontWeight: typography.fontWeight.bold,
              }}
            >
              {stat.label}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontWeight: typography.fontWeight.extrabold,
                color: stat.color,
                lineHeight: 1,
                mt: 0.5,
              }}
            >
              {stat.value}
            </Typography>
          </Card>
        ))}
      </Box>

      {/* Search & Filter Bar */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          p: 2,
          mb: 3,
          ...animations.slideUp(0.2),
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="SEARCH DELIVERIES..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            sx={{
              flex: 1,
              minWidth: 300,
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.surfaceElevated,
                border: `1px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                '& fieldset': { border: 'none' },
                '&:hover': {
                  borderColor: colors.borderLight,
                },
                '&.Mui-focused': {
                  borderColor: palette.primary,
                },
              },
              '& .MuiInputBase-input': {
                color: colors.textPrimary,
                fontWeight: typography.fontWeight.medium,
                textTransform: 'uppercase',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.textMuted }} />
                </InputAdornment>
              ),
            }}
          />
          <TableToolbar
            title=""
            searchValue=""
            onSearchChange={() => {}}
            searchPlaceholder=""
            filters={filterOptions}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            data={filteredData}
            columns={exportColumns}
            exportFileName="deliveries"
          />
        </Box>
      </Card>

      {/* Deliveries Table */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          overflow: 'hidden',
          ...animations.slideUp(0.3),
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: palette.primary }} />
          </Box>
        ) : filteredData.length === 0 ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <LocalShippingIcon sx={{ fontSize: 80, color: colors.textMuted, mb: 2 }} />
            <Typography
              variant="h5"
              sx={{
                color: colors.textPrimary,
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                mb: 1,
              }}
            >
              NO DELIVERIES FOUND
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              No deliveries match your search criteria
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table
                sx={{
                  '& .MuiTableCell-root': {
                    borderBottom: `1px solid ${colors.border}`,
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: colors.surfaceElevated,
                      borderBottom: `2px solid ${palette.primary}`,
                    }}
                  >
                    {[
                      'Delivery ID',
                      'Customer',
                      'Delivery Address',
                      'Status',
                      'Driver',
                      'Pickup Location',
                      'Created',
                      'Actions',
                    ].map((header, index) => (
                      <TableCell
                        key={header}
                        align={index === 7 ? 'center' : 'left'}
                        sx={{
                          color: colors.textMuted,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                          textTransform: 'uppercase',
                          letterSpacing: typography.letterSpacing.widest,
                          py: 2,
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.map((delivery: Delivery, index: number) => {
                    const statusColor = getStatusColor(delivery.status);
                    const createdAt = formatDateTime(delivery.createdAt);

                    return (
                      <TableRow
                        key={delivery.id}
                        sx={{
                          transition: theme.transitions.fast,
                          borderLeft: `4px solid ${statusColor}`,
                          '&:hover': {
                            backgroundColor: alpha(statusColor, 0.05),
                          },
                          ...animations.tableRowFade(index),
                        }}
                      >
                        {/* Delivery ID */}
                        <TableCell sx={{ py: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: typography.fontFamily.mono,
                              color: colors.textPrimary,
                              fontSize: typography.fontSize.sm,
                              fontWeight: typography.fontWeight.bold,
                              letterSpacing: typography.letterSpacing.wide,
                            }}
                          >
                            {delivery.id.substring(0, 8).toUpperCase()}
                          </Typography>
                        </TableCell>

                        {/* Customer */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ fontSize: 18, color: palette.primary }} />
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: typography.fontFamily.display,
                                  fontWeight: typography.fontWeight.semibold,
                                  color: colors.textPrimary,
                                  fontSize: typography.fontSize.sm,
                                }}
                              >
                                {delivery.customerName || 'N/A'}
                              </Typography>
                              {delivery.customerPhone && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontFamily: typography.fontFamily.mono,
                                    color: colors.textMuted,
                                    fontSize: '11px',
                                  }}
                                >
                                  {delivery.customerPhone}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Delivery Address */}
                        <TableCell>
                          <Tooltip title={formatAddress(delivery.deliveryAddress)}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocationOnIcon sx={{ fontSize: 16, color: palette.error }} />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: typography.fontFamily.display,
                                  color: colors.textPrimary,
                                  fontSize: typography.fontSize.sm,
                                  maxWidth: 200,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {formatAddress(delivery.deliveryAddress)}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>

                        {/* Status */}
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(delivery.status) || undefined}
                            label={formatStatusLabel(delivery.status)}
                            sx={{
                              backgroundColor: alpha(statusColor, 0.1),
                              color: statusColor,
                              fontFamily: typography.fontFamily.mono,
                              fontSize: '10px',
                              fontWeight: typography.fontWeight.bold,
                              border: `1px solid ${statusColor}`,
                              '& .MuiChip-icon': {
                                color: statusColor,
                              },
                            }}
                          />
                        </TableCell>

                        {/* Driver */}
                        <TableCell>
                          {delivery.driver ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar
                                sx={{
                                  width: 28,
                                  height: 28,
                                  backgroundColor: palette.info,
                                  fontSize: '12px',
                                }}
                              >
                                {delivery.driver.firstName?.[0]}
                                {delivery.driver.lastName?.[0]}
                              </Avatar>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: typography.fontFamily.display,
                                  color: colors.textPrimary,
                                  fontSize: typography.fontSize.sm,
                                }}
                              >
                                {delivery.driver.firstName} {delivery.driver.lastName}
                              </Typography>
                            </Box>
                          ) : (
                            <Chip
                              label="UNASSIGNED"
                              size="small"
                              sx={{
                                backgroundColor: alpha(palette.warning, 0.1),
                                color: palette.warning,
                                fontFamily: typography.fontFamily.mono,
                                fontSize: '10px',
                                fontWeight: typography.fontWeight.bold,
                                border: `1px solid ${palette.warning}`,
                              }}
                            />
                          )}
                        </TableCell>

                        {/* Pickup Location */}
                        <TableCell>
                          {delivery.pickupLocation ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocalShippingIcon sx={{ fontSize: 16, color: palette.info }} />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: typography.fontFamily.display,
                                  color: colors.textPrimary,
                                  fontSize: typography.fontSize.sm,
                                }}
                              >
                                {delivery.pickupLocation.name}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{ color: colors.textMuted, fontSize: typography.fontSize.sm }}
                            >
                              -
                            </Typography>
                          )}
                        </TableCell>

                        {/* Created */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTimeIcon sx={{ fontSize: 16, color: colors.textMuted }} />
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: typography.fontFamily.display,
                                  fontWeight: typography.fontWeight.semibold,
                                  color: colors.textPrimary,
                                  fontSize: typography.fontSize.sm,
                                  lineHeight: 1.2,
                                }}
                              >
                                {createdAt.date}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontFamily: typography.fontFamily.mono,
                                  color: colors.textMuted,
                                  fontSize: typography.fontSize.xs,
                                }}
                              >
                                {createdAt.time}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/app/deliveries/${delivery.id}`)}
                              sx={{
                                border: `1px solid ${colors.border}`,
                                borderRadius: theme.radius.xs,
                                color: colors.textPrimary,
                                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                '&:hover': {
                                  borderColor: palette.primary,
                                  backgroundColor: alpha(palette.primary, 0.1),
                                  color: palette.primary,
                                  transform: 'scale(1.1)',
                                },
                                '&:active': {
                                  transform: 'scale(0.95)',
                                },
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Industrial Pagination */}
            <Box
              sx={{
                backgroundColor: colors.surfaceElevated,
                borderTop: `2px solid ${colors.border}`,
              }}
            >
              <TablePagination
                component="div"
                count={filteredData.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    color: colors.textPrimary,
                    fontFamily: typography.fontFamily.mono,
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    fontWeight: typography.fontWeight.bold,
                    textTransform: 'uppercase',
                    letterSpacing: typography.letterSpacing.wide,
                  },
                  '& .MuiSelect-select': {
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.sm,
                    fontWeight: typography.fontWeight.bold,
                  },
                }}
              />
            </Box>
          </>
        )}
      </Card>
    </Box>
  );
};

// Legacy export for backward compatibility
export const DeliveryList = DeliveryOverview;
